import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AuthOptionsButtons.css";
import { getAnalytics, logEvent } from "firebase/analytics";
import { isDevEnvironment } from "env/runtimeEnv";
import { useVisualViewportLayout } from "hooks/useVisualViewportLayout";
import { useAppState } from "@/contexts/UserContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTeam } from "@/contexts/TeamContext";
import { MultiSelect } from "@/features/teams/components/MultiSelect";
import { dayOptions } from "@/features/teams/config/dayOptions";
import { setAccountOrganization } from "@/api/accountApi";
import { addOrganization } from "@/api/organizationApi";
import * as Sentry from "@sentry/react";

interface AuthOptionsButtonsProps {
  userId: string;
  onSubmit: (organizationName: string) => Promise<string>;
  onOptionSelect: (option: number) => void;
  isLoading: boolean;
  isOnline: boolean;
  loadingStates: any;
}

export default function AuthOptionsButtons({
  onSubmit,
  isLoading,
  loadingStates,
}: AuthOptionsButtonsProps) {
  const navigate = useNavigate();
  const { bottomInset } = useVisualViewportLayout();
  const analytics = getAnalytics();
  logEvent(analytics, "select_content", {
    content_type: "image",
    content_id: "P12453",
  });

  const [organizationName, onOrganizationNameChange] = useState("");
  const [teamName, onTeamNameChange] = useState("");

  const { refreshData } = useAppState();
  const { userId, setAccountOrganizationId } = useAuth();
  const {
    setOrganizationId,
    setOrganizationName,
    setTeamId,
    setTeamName,
    setTeams,
    setTeamMembers,
    setIsLead,
  } = useTeam();


  async function handleSubmit() {
    addOrganization({ organizationName, teamName })
      .then((response) => {
        console.log("blabla Created organization ID:", response);
        const organizationId = response.organizationId;
        const teamId = response.teamId;

        const newMember = {
          id: userId,
          uid: userId,
          email: userId,
          organizationId,
          teamId: teamId,
          status: "active",
          isLead: true,
        };

        setTeamId(teamId);
        setTeamName(teamName);
        setIsLead(true);
        setOrganizationName(organizationName);

        setTeams((prev: { id?: string }[]) => {
          const list = Array.isArray(prev) ? prev : [];
          if (list.some((team) => team?.id === "1")) {
            return list;
          }
          return [...list];
        });
        setTeamMembers([newMember]);
        console.log("checkOut org set to " + organizationId);
        setOrganizationId(organizationId);

        setAccountOrganizationId(organizationId);
        setAccountOrganization({ organizationId }).catch((error) => {
          console.warn("Failed to persist organizationId on account:", error);
        });

        navigate("/app/home", { replace: true });
      })
      .catch((error) => {
        console.error("Error creating organization:", error);
      });

  }

  const [step, setStep] = useState(0);

  const data = [
    {
      name: "Joining existing team?",
      input: "text",
      step: 0,
    },
    {
      name: "Your organization",
      input: "text",
      step: 1,
    },
    {
      name: "Team name",
      input: "text",
      step: 2,
    },
    {
      name: "Check-in days !",
      input: "text",
      step: 3,
    },
  ];

  Sentry.logger.info("User saved feeling", {
    log_source: "sentry_test",
  });

  const visibleAvailableOptions = dayOptions.map((option) =>
    option.link === "/app/settings/teams"
      ? {
          ...option,
          state: { ...option.state },
        }
      : option
  );

  return (
    <div className="auth-options-container">
      <div className="auth-options-content">
        <div className="auth-options-header">
          <div className="logo-container">
            <div className="logo-wrapper">
              <img
                src="/logo512.png"
                alt="Experience App Icon"
                className="logo-image"
              />
            </div>
          </div>

          {isDevEnvironment && <h1 className="auth-env-label">DEV</h1>}

          <h1 className="welcome-title">{data[step].name}</h1>
        </div>
        <div className="auth-email-form-wrapper">
          {step === 0 && (
            <>
                <p
                onClick={() => {
                    navigate("/app/join-team")
                }}
                >Join existing workspace</p>
                <p
                onClick={() => {
                    setStep(1)
                }}
                >Create workspace</p>
            </>
          )}


          {step === 1 && (
            <input
              type="organizationName"
              value={organizationName || ""}
              onChange={(e) => onOrganizationNameChange(e.target.value)}
              placeholder="organiaztionName input"
              className="auth-email-input"
            />
          )}
        </div>
        <div className="auth-email-form-wrapper">
          {step === 3 && (
            <>
              <MultiSelect options={visibleAvailableOptions} />
            </>
          )}
        </div>
        <div className="auth-email-form-wrapper">
          {step === 2 && (
            <input
              type="teamName"
              value={teamName || ""}
              onChange={(e) => onTeamNameChange(e.target.value)}
              placeholder="teamName input"
              className="auth-email-input"
            />
          )}
        </div>
      </div>

      <div
        className="auth-options-footer"
        style={
          { "--keyboard-inset": `${bottomInset}px` } as React.CSSProperties
        }
      >
        {!loadingStates?.users && (
          <button
            onClick={() => {
              if (step < 2) {
                setStep(step + 1);
              } else {
                handleSubmit();
              }
            }}
            disabled={
              (!teamName && step === 2) ||
              (step === 1 && !organizationName) ||
              isLoading
            }
            className="proceed-button auth-options-submit"
          >
            {isLoading ? "Sending..." : "Continue"}
          </button>
        )}
      </div>
    </div>
  );
}
